import styled from '@emotion/styled';
import { useEffect, useMemo, useRef, type FC } from 'react';

import {
  usePagesContext,
  usePagesDispatchContext,
} from '@/components/common/providers/pages-provider';
import { DEFAULT_PAGES } from '@/constants';
import { theme } from '@/styles';
import type { SiteContentPageType } from '@/types/custom-types';
import { Page } from './page';

type Props = {
  className?: string;
  pages: SiteContentPageType[];
  currentIndex: number;
};

const NumberOfBasePages = 5;
const headerWidth = theme.structure.page.headerWidth.pc;
const contentWidthCss = `calc(100% - ${theme.structure.main.margin.horizontal.pc}px - ${headerWidth * NumberOfBasePages}px)`;

const useWrapperWidth = () => {
  const dispatch = usePagesDispatchContext();
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!wrapperRef.current || !dispatch) return;
    dispatch({
      type: 'set',
      payload: { width: wrapperRef.current.getBoundingClientRect().width },
    });
  }, [dispatch]);

  useEffect(() => {
    if (!wrapperRef.current || !dispatch) return;
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(([entry]) => {
      dispatch({
        type: 'set',
        payload: { width: entry.contentRect.width },
      });
    });

    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, [dispatch]);

  return { wrapperRef };
};

let prevAnimationStyles: { x: number }[] | null = null;

type UsePageAnimationStylesProps = Pick<Props, 'pages' | 'currentIndex'>;

const usePageAnimationStyles = ({
  pages,
  currentIndex,
}: UsePageAnimationStylesProps) => {
  const { width: wrapperWidth } = usePagesContext();
  const contentWidth = wrapperWidth - headerWidth * NumberOfBasePages;
  const widthReady = wrapperWidth > 0;

  return useMemo(() => {
    if (!widthReady) {
      return {
        widthReady,
        animationStyles: null,
        prevAnimationStyles: null,
      };
    }

    const animationStyles = pages.map((page, index) => {
      if (currentIndex >= 0 && index > currentIndex) {
        const x =
          index <= NumberOfBasePages
            ? headerWidth * index + contentWidth
            : headerWidth * NumberOfBasePages + contentWidth;
        return { x };
      } else {
        const x =
          index <= NumberOfBasePages
            ? headerWidth * index
            : headerWidth * NumberOfBasePages;
        return { x };
      }
    });

    const returnStyles = {
      widthReady,
      animationStyles,
      prevAnimationStyles,
    };

    prevAnimationStyles = animationStyles;

    return returnStyles;
  }, [contentWidth, currentIndex, pages, widthReady]);
};

export const Pages: FC<Props> = ({ className, pages, currentIndex }) => {
  const { wrapperRef } = useWrapperWidth();
  const { widthReady, animationStyles, prevAnimationStyles } =
    usePageAnimationStyles({
      pages,
      currentIndex,
    });

  return (
    <Wrapper
      className={`${className ?? ''} current-index-${
        currentIndex >= 0 ? currentIndex : 'home'
      }`}
      ref={wrapperRef}
    >
      {pages.map(({ name, to, label, component, isSingle }, index) => (
        <StyledPage
          key={`site-content-item-${name}`}
          name={name}
          to={to}
          label={label}
          initialStyle={
            widthReady && prevAnimationStyles
              ? prevAnimationStyles[index]
              : undefined
          }
          animationStyle={
            widthReady && animationStyles ? animationStyles[index] : undefined
          }
          isSingle={isSingle}
        >
          {component && component}
        </StyledPage>
      ))}
    </Wrapper>
  );
};

const StyledPage = styled(Page)`
  position: absolute;
  z-index: 1;
  top: 0;

  &.page-home {
    z-index: 2;
  }
`;

// NOTE:
// かなり複雑だが、これでアクセス時のスタイルのずれを防ぐことができる。
// もう少し分かりやすい実装がベストだが、AIに生成させてもバグるので、
// 今回はこれでいく。もう少しAIが賢くなったら、スタイルのずれを防ぎつつ、もっとシンプルな実装にリファクタリングすることを検討する。
const getBaseX = (index: number) =>
  Math.min(index, NumberOfBasePages) * headerWidth;

const getFallbackX = (index: number, currentIndex: number | 'home'): string => {
  const baseX = getBaseX(index);

  if (currentIndex === 'home' || index <= currentIndex) {
    return `${baseX}px`;
  }

  return `calc(${baseX}px + var(--content-width))`;
};

const getFallbackTransformStyles = () => {
  const currentStates: Array<number | 'home'> = [
    'home',
    ...DEFAULT_PAGES.slice(0, -1).map((_, index) => index),
  ];

  return currentStates
    .map(
      (currentState) => `
        &.current-index-${currentState} {
          ${DEFAULT_PAGES.map(
            (_, index) => `
              ${StyledPage}:nth-of-type(${index + 1}) {
                transform: translateX(${getFallbackX(index, currentState)});
              }
            `
          ).join('\n')}
        }
      `
    )
    .join('\n');
};

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  --content-width: ${contentWidthCss};

  ${getFallbackTransformStyles()}

  ${StyledPage} {
    --diff: ${({ theme }) => theme.structure.page.margin.top.pc}px;
    will-change: transform;

    &:nth-of-type(2) {
      top: calc(var(--diff));
      height: calc(100% - var(--diff));
    }

    &:nth-of-type(3) {
      top: calc(var(--diff) * 2);
      height: calc(100% - var(--diff) * 2);
    }

    &:nth-of-type(4) {
      top: calc(var(--diff) * 3);
      height: calc(100% - var(--diff) * 3);
    }

    &:nth-of-type(5) {
      top: calc(var(--diff) * 4);
      height: calc(100% - var(--diff) * 4);
    }
  }
`;

export default Pages;
